import React, { useEffect, useState } from 'react';
import { Box, Button, createStyles, Grid, makeStyles, TextField } from "@material-ui/core";
import { Add, Search } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { connect } from "react-redux";
import { ApplicationState } from "../../redux/reducers";
import { ICliente } from "../../entities/catalogos/ICliente";
import { IUsuario } from "../../entities/catalogos/IUsuario";
import { IGuiaWizard } from "../../entities/guias/IGuiaWizard";
import { IRecoleccion } from "../../redux/types/recoleccionActionTypes";
import { setGuiaDestino, setGuiaWizard } from "../../redux/actions/guiaActions";
import AutocompleteControlSolicitud, { IAutocompleteSolicitud } from "../controls/AutocompleteControlSolicitud";
import TipoRecoleccionLista from "./TipoRecoleccionLista";
import SucursalComponente from "./Sucursal";
import AgregarDireccion from "./AgregarDireccion";
import AgregarContacto from "./AgregarContacto";
import RecoleccionDireccion from "./RecoleccionDireccion";
import RecoleccionContacto from "./RecoleccionContacto";
import useCommonApi from "../../api/useCommonApi";
import { AreaServicio } from "../../entities/catalogos/AreaServicio";
import { ServicioAdicional } from "../../entities/catalogos/ServicioAdicional";
import { Sucursal } from "../../entities/catalogos/Sucursal";
import { Colonia } from "../../entities/catalogos/Colonia";
import { initialRecoleccionState } from "../../redux/reducers/recoleccionReducer";
import { getDireccionDistribuidor } from "../../blocks/utility";
import { enums } from "../../enums/enums";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";
import swal from 'sweetalert2';
import LoadingDialog from "../controls/LoadingDialog";
import RecoleccionFechaEntregaGuia from "./RecoleccionFechaEntregaGuia";
import {
    calcularAceptaCitaEadDestino,
    limpiarModalidadEntregaGuiaDestino,
    prepararAreaServicioDestinoGuia
} from "./guiaRecoleccionOrigenHelpers";
import {
    fusionarServiciosDestinoGuia,
    obtenerServiciosAdicionalesOrigenDestinoGuia
} from "./guiaServiciosOrigenDestinoHelpers";
import ServicioAdicionalDestinoGuia from "./ServicioAdicionalDestinoGuia";
import NextButton from "../controls/NextButton";

interface StateProps {
    cliente: ICliente;
    usuario: IUsuario;
    guiaWizard: IGuiaWizard;
    guiaOrigen: IRecoleccion;
    guiaDestino: IRecoleccion;
    ayuda: any;
}

interface DispatchProps {
    setGuiaDestino: typeof setGuiaDestino;
    setGuiaWizard: typeof setGuiaWizard;
}

type Props = StateProps & DispatchProps & {
    onTerminarDestino?: () => void;
};

const useStyles = makeStyles(() =>
    createStyles({
        icon: {
            width: "40px",
            height: "40px",
            minWidth: "40px",
            borderRadius: "20px",
            backgroundColor: "#19754e",
            color: "#fff",
            textAlign: "center",
            paddingTop: "5px"
        }
    }),
);

const crearSucursalSegura = (sucursal?: Sucursal): Sucursal | undefined => {
    if (!sucursal?.id) {
        return undefined;
    }
    try {
        return new Sucursal(sucursal);
    } catch {
        return sucursal;
    }
};

const construirCodigoPostalInicialGuia = (destino: IRecoleccion): IAutocompleteSolicitud | null => {
    if (!destino?.codigoPostal || destino.codigoPostal === "") {
        return null;
    }
    return {
        id: destino.colonia?.id ? destino.colonia.id : 0,
        colonia: {},
        calle: destino.distribuidor?.calle ?? "",
        codigoPostal: destino.codigoPostal.toString(),
        nombreClienteDistribuidor: destino.distribuidor?.nombre
            ?? destino.distribuidor?.nombreClienteDistribuidor
            ?? "",
        parentId: Number(destino.codigoPostal)
    };
};

const resolverPaisIdOpcionDestino = (option: any): number => {
    const raw = option?.paisID ?? option?.paisId ?? option?.colonium?.pais?.id ?? option?.colonia?.pais?.id ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
};

const esDestinoMexico = (option: any): boolean =>
    resolverPaisIdOpcionDestino(option) === enums.pais.mexico;

const filtrarOpcionesDestinoMexico = (options: IAutocompleteSolicitud[]): IAutocompleteSolicitud[] =>
    (options ?? []).filter(o => esDestinoMexico(o));

const mensajeDestinoInternacionalNoPermitido = (): string =>
    localStorage.getItem("idioma") === "en-US"
        ? "The selected destination is not available for guide generation."
        : "El destino seleccionado no está disponible para la generación de guías.";

const DestinoGuia = ({ cliente, usuario, guiaWizard, guiaOrigen, guiaDestino, ayuda, setGuiaDestino, setGuiaWizard, onTerminarDestino }: Props) => {
    const classes = useStyles();
    const apiSource = useCommonApi();
    const [codigoPostal, setCodigoPostal] = useState<IAutocompleteSolicitud | null>(
        () => construirCodigoPostalInicialGuia(guiaDestino)
    );
    const [contactos, setContactos] = useState<any[]>([]);
    const [nuevaDireccion, setNuevaDireccion] = useState(false);
    const [nuevoContacto, setNuevoContacto] = useState(false);
    const [buscaDireccion, setBuscaDireccion] = useState(false);
    const [buscaContacto, setBuscaContacto] = useState(false);
    const [loading, setLoading] = useState(false);

    const cargarContactosDistribuidor = async (destinatarioId: number): Promise<any[]> => {
        const lista = await apiSource.obtenerAsync<any>(
            "cliente-distribuidor-contacto/by-distribuidor-client-id",
            { clienteId: cliente.id, clienteDistribuidorID: destinatarioId }
        );
        return lista || [];
    };

    useEffect(() => {
        const destinatarioId = guiaDestino.distribuidor?.clienteDistribuidorID;
        if (!destinatarioId) {
            return;
        }
        let cancelled = false;
        cargarContactosDistribuidor(destinatarioId)
            .then(lista => {
                if (!cancelled) {
                    setContactos(lista);
                }
            })
            .catch(() => {
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const actualizarFlagsDestino = (tipoRecoleccionId?: number, destinatarioId?: number) => {
        const tipo = tipoRecoleccionId || 0;
        const esOcurre = tipo === enums.tipoRecoleccion.ocurre ? 1 : 0;
        const esEad = tipo === enums.tipoRecoleccion.comercial || tipo === enums.tipoRecoleccion.residencial ? 1 : 0;
        setGuiaWizard({
            ...guiaWizard,
            destinatarioId: destinatarioId ?? guiaWizard.destinatarioId,
            esEad,
            esOcurreDestino: esOcurre
        });
    };

    const resolverAreaServicio = async (optionSelected, colonia: Colonia) => {
        const params = {
            paisId: colonia.pais?.id,
            estadoId: colonia.estado?.id,
            ciudadId: colonia.ciudad?.id,
            codigoPostal: colonia?.codigoPostal,
            empresaId: cliente?.empresaID,
            ClienteID: cliente?.id,
            ClienteDistribuidorID: optionSelected.clienteDistribuidorID ?? optionSelected.clienteDistribuidorId
        };
        return await apiSource.obtenerAsync<AreaServicio>("area-servicio/by-address-cliente-distribuidor", params);
    };

    const aplicarDestino = async (optionSelected) => {
        if (!optionSelected) {
            setGuiaDestino({ ...initialRecoleccionState });
            actualizarFlagsDestino(undefined, 0);
            return;
        }

        if (!esDestinoMexico(optionSelected)) {
            swal.fire({
                titleText: traducirMessage(276),
                text: mensajeDestinoInternacionalNoPermitido(),
                type: "error",
            });
            setCodigoPostal(null);
            setGuiaDestino({ ...initialRecoleccionState });
            actualizarFlagsDestino(undefined, 0);
            return;
        }

        setLoading(true);
        try {
            const colonia = new Colonia(optionSelected.colonium);
            const destinatarioId = optionSelected.clienteDistribuidorID ?? optionSelected.clienteDistribuidorId;
            const result = await resolverAreaServicio(optionSelected, colonia);
            const areaServicio = result ? new AreaServicio(result) : undefined;
            const sucursal = crearSucursalSegura(areaServicio?.sucursal);
            const sinCobertura = !areaServicio?.id;

            const destino: IRecoleccion = {
                ...initialRecoleccionState,
                paisId: optionSelected.paisID || 1,
                codigoPostal: optionSelected.codigoPostal || '',
                direccion: optionSelected.direccion || getDireccionDistribuidor(optionSelected),
                colonia,
                distribuidor: {
                    ...optionSelected,
                    clienteDistribuidorID: destinatarioId,
                    nombre: optionSelected.nombreClienteDistribuidor ?? optionSelected.nombre
                },
                areaServicio,
                sucursal,
                sinAreaServicio: sinCobertura,
                tipoRecoleccionId: undefined,
                completado: false
            };

            setGuiaDestino(destino);
            actualizarFlagsDestino(undefined, destinatarioId);

            if (sinCobertura) {
                swal.fire({
                    titleText: traducirMessage(276),
                    text: traducirMessage(193),
                    type: 'error',
                });
            }

            const lista = await cargarContactosDistribuidor(destinatarioId);
            setContactos(lista);
        } catch {
            swal.fire({
                titleText: traducirMessage(276),
                text: localStorage.getItem("idioma") === 'en-US'
                    ? 'The destination coverage could not be resolved.'
                    : 'No fue posible resolver la cobertura del destino.',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCodigoPostal = async (_event, optionSelected) => {
        setCodigoPostal(optionSelected);
        if (optionSelected != null) {
            await aplicarDestino(optionSelected);
        }
    };

    const aplicarDireccionCreada = async (direccion: any) => {
        if (!direccion) {
            return;
        }
        if (!esDestinoMexico(direccion)) {
            swal.fire({
                titleText: traducirMessage(276),
                text: mensajeDestinoInternacionalNoPermitido(),
                type: "warning",
            });
            return;
        }
        await handleCodigoPostal(null, direccion);
    };

    const resolverEsServicioInternacional = (destino: IRecoleccion) =>
        (destino.paisId === enums.pais.usa) || !!guiaOrigen.esServicioInternacional;

    const prepararDestinoConArea = (
        destino: IRecoleccion,
        areaServicio?: AreaServicio,
        tipoRecoleccionId?: number
    ): IRecoleccion => {
        const areaPreparada = areaServicio ? prepararAreaServicioDestinoGuia(areaServicio) : destino.areaServicio;
        const esServicioInternacional = resolverEsServicioInternacional(destino);
        const catalogoServicios = obtenerServiciosAdicionalesOrigenDestinoGuia(
            areaPreparada?.serviciosAdicionales as ServicioAdicional[],
            false,
            cliente,
            areaPreparada
        );
        const serviciosAdicionales = fusionarServiciosDestinoGuia(
            catalogoServicios,
            destino.serviciosAdicionales as ServicioAdicional[]
        );
        const base = limpiarModalidadEntregaGuiaDestino({
            ...destino,
            tipoRecoleccionId: tipoRecoleccionId ?? destino.tipoRecoleccionId,
            areaServicio: areaPreparada,
            sucursal: crearSucursalSegura(areaPreparada?.sucursal) || destino.sucursal,
            sinAreaServicio: !(areaPreparada?.id),
            esServicioInternacional,
            aceptaCita: calcularAceptaCitaEadDestino(cliente, areaPreparada, esServicioInternacional),
            serviciosAdicionales,
            completado: false
        });
        return base;
    };

    const handleTipoRecoleccion = async (tipoRecoleccionId: number) => {
        if (guiaDestino.sinAreaServicio && tipoRecoleccionId !== enums.tipoRecoleccion.ocurre) {
            swal.fire({
                titleText: traducirMessage(276),
                text: traducirMessage(193),
                type: 'error',
            });
            return;
        }

        let destino = limpiarModalidadEntregaGuiaDestino({
            ...guiaDestino,
            tipoRecoleccionId,
            completado: false
        });

        if (tipoRecoleccionId === enums.tipoRecoleccion.ocurre) {
            const areaServicioId = guiaDestino.sucursal?.ocurreAreaServicioId || 0;
            if (areaServicioId) {
                const result: AreaServicio = await apiSource.obtenerPorIdAsync("area-servicio", areaServicioId);
                const areaServicio = result ? new AreaServicio(result) : undefined;
                destino = {
                    ...destino,
                    areaServicio,
                    sucursal: crearSucursalSegura(areaServicio?.sucursal) || guiaDestino.sucursal,
                    sinAreaServicio: !(areaServicio?.id),
                    aceptaCita: false,
                    serviciosAdicionales: [],
                    completado: !!(destino.sucursal?.id || guiaDestino.sucursal?.id)
                };
            }
        } else if (guiaDestino.colonia) {
            const result = await resolverAreaServicio(guiaDestino.distribuidor, guiaDestino.colonia);
            const areaServicio = result ? new AreaServicio(result) : undefined;
            destino = prepararDestinoConArea(destino, areaServicio, tipoRecoleccionId);
        }

        setGuiaDestino(destino);
        actualizarFlagsDestino(tipoRecoleccionId, guiaWizard.destinatarioId);
    };

    const handleSucursal = async (sucursal: Sucursal | undefined) => {
        if (!sucursal) {
            return;
        }
        let destino = { ...guiaDestino, sucursal, completado: true };
        if (sucursal?.ocurreAreaServicioId) {
            const result: AreaServicio = await apiSource.obtenerPorIdAsync("area-servicio", sucursal.ocurreAreaServicioId);
            const areaServicio = result ? new AreaServicio(result) : undefined;
            destino = { ...destino, areaServicio, sinAreaServicio: !(areaServicio?.id) };
        }
        setGuiaDestino(destino);
    };

    const handleContacto = (contacto) => {
        setGuiaDestino({ ...guiaDestino, contacto });
    };

    const mostrarAltaDireccion = cliente.esPermitirAgregarDirecciones && !cliente.esMostrarDistribuidoresDestinatario;

    return (<>
        {loading && <LoadingDialog titulo={'Procesando'} abierto={true} />}
        <Grid container spacing={2} alignItems="center">
            <Grid item sm={10}>
                <strong>{traducirMessage(164)}</strong>
                <AutocompleteControlSolicitud
                    name="codigoPostalDestinoGuia"
                    source="cliente-distribuidor/by-codigo-postal"
                    label=""
                    value={codigoPostal}
                    variant="outlined"
                    onChange={handleCodigoPostal}
                    filter={{ paisId: 0, clienteId: cliente.id, esOrigen: false }}
                    filterResults={filtrarOpcionesDestinoMexico}
                />
            </Grid>
            <Grid item xs={1}>
                <Button className={classes.icon} color="primary" variant="contained" onClick={() => setBuscaDireccion(true)}>
                    <Search />
                </Button>
            </Grid>
            <Grid item xs={1}>
                {mostrarAltaDireccion &&
                    <Button className={classes.icon} color="primary" variant="contained" onClick={() => setNuevaDireccion(true)}>
                        <Add />
                    </Button>
                }
            </Grid>
        </Grid>

        {guiaDestino.codigoPostal !== "" &&
            <Box mt={2} mb={2}>
                <Box fontWeight="fontWeightMedium">
                    {guiaDestino.distribuidor?.nombre || guiaDestino.distribuidor?.nombreClienteDistribuidor}
                </Box>
                <Box fontWeight="fontWeightLight" fontSize={12}>
                    {guiaDestino.direccion}
                </Box>
            </Box>
        }

        {guiaDestino.distribuidor?.clienteDistribuidorID > 0 &&
            <Grid container spacing={2} alignItems="center">
                <Grid item sm={10}>
                    <Box mb={1} style={{ fontWeight: 600 }}>{traducirLabel(50)}</Box>
                    <Autocomplete
                        options={contactos}
                        getOptionLabel={(option) => option.nombreClienteDistribuidorContacto || option.nombre || ''}
                        value={guiaDestino.contacto || null}
                        onChange={(_event, value) => handleContacto(value)}
                        renderInput={(params) => <TextField {...params} variant="outlined" />}
                    />
                </Grid>
                <Grid item xs={1}>
                    <Button className={classes.icon} color="primary" variant="contained" onClick={() => setBuscaContacto(true)}>
                        <Search />
                    </Button>
                </Grid>
                <Grid item xs={1}>
                    <Button className={classes.icon} color="primary" variant="contained" onClick={() => setNuevoContacto(true)}>
                        <Add />
                    </Button>
                </Grid>
            </Grid>
        }

        {guiaDestino.distribuidor?.clienteDistribuidorID > 0 &&
            <Grid container spacing={2} style={{ marginTop: 16 }}>
                <TipoRecoleccionLista
                    value={guiaDestino.tipoRecoleccionId}
                    onClick={handleTipoRecoleccion}
                    recoleccion={guiaDestino}
                    mostrarResidencial={true}
                    esOrigen={false}
                    ayuda={ayuda}
                    idiomaId={""}
                />
            </Grid>
        }

        {guiaDestino.tipoRecoleccionId === enums.tipoRecoleccion.ocurre &&
            <SucursalComponente value={guiaDestino.sucursal} esOrigen={false} onChange={handleSucursal} />
        }

        {guiaDestino.tipoRecoleccionId !== undefined
            && guiaDestino.tipoRecoleccionId !== enums.tipoRecoleccion.ocurre
            && !guiaDestino.sinAreaServicio
            && !!(guiaDestino.areaServicio?.id) &&
            <RecoleccionFechaEntregaGuia
                guiaOrigen={guiaOrigen}
                guiaDestino={guiaDestino}
                cliente={cliente}
                onChange={setGuiaDestino}
            />
        }

        <ServicioAdicionalDestinoGuia />

        {guiaDestino.completado
            && (!guiaDestino.sinAreaServicio || guiaDestino.esServicioInternacional || !!guiaDestino.sucursal)
            && onTerminarDestino &&
            <Grid container style={{ width: "100%", textAlign: "right", paddingTop: "16px" }}>
                <Grid item xs={12}>
                    <NextButton nextStep={onTerminarDestino} />
                </Grid>
            </Grid>
        }

        {nuevaDireccion &&
            <AgregarDireccion
                abierto={nuevaDireccion}
                confirmar={() => setNuevaDireccion(false)}
                cerrar={() => setNuevaDireccion(false)}
                cliente={cliente}
                usuario={usuario}
                esOrigen={false}
                esDestino={true}
                setDistribuidor={aplicarDireccionCreada}
                ayuda={ayuda}
            />
        }
        {nuevoContacto &&
            <AgregarContacto
                abierto={nuevoContacto}
                confirmar={() => setNuevoContacto(false)}
                cerrar={() => setNuevoContacto(false)}
                cliente={cliente}
                usuario={usuario}
                distribuidor={guiaDestino.distribuidor}
                setContacto={(contacto) => {
                    handleContacto(contacto);
                    setContactos([...(contactos || []), contacto]);
                }}
                ayuda={ayuda}
            />
        }
        {buscaDireccion &&
            <RecoleccionDireccion
                abierto={buscaDireccion}
                confirmar={() => setBuscaDireccion(false)}
                cerrar={() => setBuscaDireccion(false)}
                setDistribuidor={(direccion) => handleCodigoPostal(null, direccion)}
                cliente={cliente}
                esOrigen={false}
                ayuda={ayuda}
                usuario={usuario}
            />
        }
        {buscaContacto &&
            <RecoleccionContacto
                abierto={buscaContacto}
                confirmar={() => setBuscaContacto(false)}
                cerrar={() => setBuscaContacto(false)}
                esOrigen={false}
                setContacto={(contacto) => handleContacto(contacto)}
                cliente={cliente}
                usuario={usuario}
                distribuidor={guiaDestino.distribuidor}
                ayuda={ayuda}
            />
        }
    </>);
};

const mapStateToProps = (state: ApplicationState) => ({
    cliente: state.cliente,
    usuario: state.usuario,
    guiaWizard: state.guiaWizard,
    guiaOrigen: state.guiaOrigen,
    guiaDestino: state.guiaDestino,
    ayuda: state.ayuda
});

const mapDispatchToProps = {
    setGuiaDestino,
    setGuiaWizard
};

export default connect(mapStateToProps, mapDispatchToProps)(DestinoGuia);
